'use client';

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { useTranslation } from '@/hooks/useTranslation';

export interface ToastProps {
  id: string;
  title: string;
  description?: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  onClose?: () => void;
}

const Toast: React.FC<ToastProps> = ({
  title,
  description,
  type = 'info',
  duration = 5000,
  onClose,
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(true);

  // Helper function to translate common error messages
  const translateMessage = (message: string): string => {
    // Map common English error messages to translation keys
    const messageMap: Record<string, string> = {
      'Network error': t('auth.signIn.networkError'),
      'Network error: Failed to fetch': t('auth.signIn.networkError'),
      'Unable to connect to the server. Please check your internet connection and try again.':
        t('auth.signIn.networkError'),
      Error: t('common.error'),
      Unauthorized: t('unauthorized.title'),
      'Please log in to access this resource': t('unauthorized.message'),
      'Not Found': t('errorPage.404.title'),
      'The requested resource was not found': t('errorPage.404.message'),
      'Server Error': t('errorPage.500.title'),
      'An internal server error occurred': t('errorPage.500.message'),
      'Validation Error': t('validation.errorTitle'),
      'The provided data is invalid': t('validation.generic'),
    };

    return messageMap[message] || message;
  };

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onClose?.(), 300);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const getToastStyles = () => {
    const baseStyles = 'p-4 rounded-lg shadow-lg border max-w-md mx-auto';

    switch (type) {
      case 'success':
        return `${baseStyles} bg-success-light/90 border-success text-success`;
      case 'error':
        return `${baseStyles} bg-danger-light/90 border-danger text-danger`;
      case 'warning':
        return `${baseStyles} bg-warning-light/90 border-warning text-warning`;
      case 'info':
      default:
        return `${baseStyles} bg-accent-light/90 border-accent text-accent`;
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
      default:
        return 'ℹ';
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -50, scale: 0.9 }}
          className={getToastStyles()}
          role="alert"
          aria-live="polite"
        >
          <div className="flex items-start">
            <div className="flex-shrink-0 mr-3">
              <span className="text-lg font-semibold">{getIcon()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold mb-1">
                {translateMessage(title)}
              </h4>
              {description && (
                <p className="text-sm opacity-90">
                  {translateMessage(description)}
                </p>
              )}
            </div>
            {onClose && (
              <Button
                onClick={() => {
                  setIsVisible(false);
                  setTimeout(() => onClose(), 300);
                }}
                variant="ghost"
                size="icon"
                className="ml-3 flex-shrink-0 opacity-60 hover:opacity-100"
                aria-label="Close toast"
              >
                ✕
              </Button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Toast;
export { Toast };
